import { useEffect, useState, type FC } from "react";

import type {
  BillingTerm,
  PackTrialPeriod,
  PackSettingsView,
  PackResourceItem,
} from "features/Licensing/Packs/types/state";
import Dropdown from "features/Library/Common/Fields/Dropdown";
import ValidatedForm from "components/forms/ValidatedForm";
import { type FormikProps } from "formik";
import { ValidationThrottler } from "features/Library/Common/Fields/ValidationThrottler";
import { WithFormikPayload } from "features/Library/Common/Fields/WithFormikPayload";

import TrialPeriodSettings from "../../TrialPeriodSettings/TrialPeriodSettings";
import { first } from "lodash";
import { type AutosaveProps } from "utils";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { withUSFormattedNumberInput } from "components/forms/USFormattedNumberInput/USFormattedNumberInput";
import { PricingTypes } from "enums/licensing/pricingTypes";
import { Typography } from "components/common/Typography/Typography";

interface PackPricingProps extends FormikProps<PackSettingsView> {
  disabled: boolean;
  prefix: string;
  billingTerms: BillingTerm[];
  packTrialPeriods: PackTrialPeriod[];
  pricingTypes: PackResourceItem[];
  hasGlobalAccess: boolean;
  hasPacksManagePermissions: boolean;
  onIsValidChange?: (isValid: boolean) => void;
}

const USFormattedInputField = withUSFormattedNumberInput(ValidatedForm.InputField);

export const PricingSettings: FC<PackPricingProps & AutosaveProps> = ({
  disabled,
  values,
  prefix,
  billingTerms,
  pricingTypes,
  packTrialPeriods,
  onBlur,
  save,
  onIsValidChange,
  hasGlobalAccess,
  hasPacksManagePermissions,
  ...rest
}) => {
  const { pricingSettings, isFree, pricingTypeId } = values;
  const prefixedName = (name: string) => `${prefix}.${name}`;

  const [billingTerm, setBillingTerm] = useState<BillingTerm | undefined>();
  const otherPackTypeEnabled = useFeatureFlag(FeatureFlags.OtherPackType);

  useEffect(() => {
    const selectedBillingTerm =
      pricingTypeId === PricingTypes.Paid ? pricingSettings.billingTermId ?? first(billingTerms)?.id : null;
    rest.setFieldValue(prefixedName("billingTermId"), selectedBillingTerm);
    if (selectedBillingTerm != null) {
      setBillingTerm(billingTerms.find((b) => b.id === selectedBillingTerm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingTypeId, pricingSettings.billingTermId, billingTerms, setBillingTerm]);

  const pricingTypeOptions = otherPackTypeEnabled
    ? pricingTypes
        .filter((option) => hasGlobalAccess || option.id !== PricingTypes.Paid || option.id === pricingTypeId)
        .map((option) => {
          return {
            text: option.name,
            value: option.id,
          };
        })
    : [
        { text: "Free", value: true },
        { text: "Paid", value: false },
      ];

  const getPriceLabel = () => `Price/User/${billingTerm?.term || ""}`;

  const toDropdownOptions = (data: { id: number; name: string }[]) =>
    data.map((item) => ({ text: item.name, value: item.id }));

  const getValidateFieldProps = (): any => {
    const {
      errors,
      touched,
      isValid,
      isValidating,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      resetForm,
    } = rest;

    return {
      errors,
      touched,
      isFormValid: isValid && !isValidating,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      resetForm,
    };
  };

  const disableEdit = (!hasGlobalAccess && pricingTypeId === PricingTypes.Paid) || !hasPacksManagePermissions;

  const renderPricingField = () => {
    return otherPackTypeEnabled ? (
      <WithFormikPayload
        propertyName={"pricingTypeId"}
        {...rest}
        values={values}
        selfValidation
        render={(formikProps) => (
          <ValidationThrottler {...formikProps} value={pricingTypeId}>
            <Dropdown
              label="Type"
              value={pricingTypeId}
              options={pricingTypeOptions}
              errorPosition={"bottom"}
              disabled={disabled || rest.isSubmitting || disableEdit}
              setFieldTouched={rest.setFieldTouched}
              setFieldValue={rest.setFieldValue}
              onChange={async (_, data) => {
                await rest.setFieldValue("pricingTypeId", data.value);
                save();
              }}
              {...formikProps}
              propertyName={"pricingTypeId"}
              disableValidation
            />
          </ValidationThrottler>
        )}
      />
    ) : (
      <WithFormikPayload
        propertyName={"isFree"}
        {...rest}
        values={values}
        selfValidation
        render={(formikProps) => (
          <ValidationThrottler {...formikProps} value={isFree}>
            <Dropdown
              label="Type"
              value={isFree}
              options={pricingTypeOptions}
              errorPosition={"bottom"}
              disabled={disabled || !hasGlobalAccess || rest.isSubmitting}
              setFieldTouched={rest.setFieldTouched}
              setFieldValue={rest.setFieldValue}
              onChange={async (_, data) => {
                await rest.setFieldValue("isFree", data.value);
                await rest.setFieldValue("pricingTypeId", data.value ? PricingTypes.Free : PricingTypes.Paid);
                save();
              }}
              {...formikProps}
              propertyName={"isFree"}
              disableValidation
            />
          </ValidationThrottler>
        )}
      />
    );
  };

  return (
    <>
      <Typography variant="title2" bold>
        PRICING
      </Typography>
      {renderPricingField()}
      {pricingTypeId === PricingTypes.Paid && hasGlobalAccess && (
        <>
          <WithFormikPayload
            propertyName={prefixedName("billingTermId")}
            {...rest}
            values={values}
            selfValidation
            render={(formikProps) => (
              <ValidationThrottler {...formikProps} value={isFree}>
                <Dropdown
                  label="Billing Term"
                  value={pricingSettings.billingTermId!}
                  options={toDropdownOptions(billingTerms)}
                  errorPosition={"bottom"}
                  disabled={disabled || rest.isSubmitting}
                  setFieldTouched={rest.setFieldTouched}
                  setFieldValue={rest.setFieldValue}
                  onChange={save}
                  {...formikProps}
                  propertyName={prefixedName("billingTermId")}
                  disableValidation
                />
              </ValidationThrottler>
            )}
          />
          <USFormattedInputField
            label={getPriceLabel()}
            value={pricingSettings.pricePerUser}
            propertyName={prefixedName("pricePerUser")}
            markAsRequired
            disabled={disabled || rest.isSubmitting}
            placeholder="$0.00"
            onIsFormValidChange={onIsValidChange}
            decimalDigits={2}
            onValueChanged={(data) => rest.setFieldValue(prefixedName("pricePerUser"), data.value)}
            onBlur={onBlur}
            {...rest}
          />

          <TrialPeriodSettings
            values={pricingSettings}
            propertyNamePrefix={"pricingSettings"}
            trialOptions={packTrialPeriods}
            onBlur={onBlur}
            onChanged={save}
            disabled={disabled || rest.isSubmitting}
            validatedFieldProps={getValidateFieldProps()}
            overrideLabel="Offer Free Trial"
            verticalStack
          />
        </>
      )}
    </>
  );
};
